/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, DateRange, FormControl, Grid, Heading, Icon, Text, VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import DateRangeField from 'components/form/field/DateRangeField'
import { CiapCidSelectModel, isCiap } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { EquipeResponsavelField } from 'components/form/field/select/EquipeResponsavelSelectField/EquipeResponsavelField'
import { MicroareaSelectField } from 'components/form/field/select/MicroareaSelectField/MicroareaSelectField'
import { RacaCorSelectField } from 'components/form/field/select/RacaCorSelectField'
import { startOfDay, subMonths } from 'date-fns'
import { FormApi } from 'final-form'
import { AcompanhamentoCondicaoSaudeTematicaEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useServerTime } from 'hooks/useServerTime'
import { Fragment, useMemo, useState } from 'react'
import { FormRenderProps } from 'react-final-form'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { isUndefinedOrNull } from 'util/checks'
import { metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'

import { FaixaEtariaFilterGroup } from '../components/FaixaEtariaFilterGroup'
import { SexoIdentidadeGeneroButtonGroup } from '../components/SexoIdentidadeGeneroButtonGroup'
import {
  AcompanhamentoCondicaoSaudeFiltrosAvancadosModal,
  FILTROS_AVANCADOS_URL,
} from './components/AcompanhamentoCondicaoSaudeFiltrosAvancadosModal'
import { AcompanhamentoCondicaoSaudeProblemasCondicoesFilter } from './components/AcompanhamentoCondicaoSaudeProblemasCondicoesFilter'
import { ListaTematicaSection } from './components/lista-tematica/ListaTematicaSection'
import { VulnerabilidadeFilterGroup } from './components/VulnerabilidadeFilterGroup'
import {
  AcompanhamentoCondicaoSaudeFormModel,
  filterDefaultAcompanhamentoCondicaoSaude,
} from './model/model-acompanhamentoCondicaoSaude'
import { SearchFooter } from './SearchFooter'
import { contarFiltrosAvancadosAplicados } from './utils-acompanhamentoCondicaoSaude'

interface AcompanhamentoCondicaoSaudeFormProps extends FormRenderProps<AcompanhamentoCondicaoSaudeFormModel> {
  setListaTematica: (listaTematica: AcompanhamentoCondicaoSaudeTematicaEnum) => void
}

const path = metaPath<AcompanhamentoCondicaoSaudeFormModel>()

const onClearField = (form: FormApi<AcompanhamentoCondicaoSaudeFormModel>, field: string) => {
  form.change(field, undefined)
  form.blur(field)
}

export function AcompanhamentoCondicaoSaudeForm(props: AcompanhamentoCondicaoSaudeFormProps) {
  const { values, form, initialValues, handleSubmit, setListaTematica } = props
  const { acesso, hasAuthorization } = useAcessoLotacaoOrEstagio()
  const { analytics } = useFirebase()
  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  const history = useHistory()
  const match = useRouteMatch()

  const isCoordenador = hasAuthorization(Permissions.acompanhamentos.condicoesDeSaude.todasAsEquipes)

  const isAbleToBuscar = isCoordenador || values.equipeResponsavelId

  const canCheckApenasProblemasCondicoesAtivas = isEmpty(values.grupoCondicao) && isEmpty(values.problemasCondicoes)

  // TODO (Royal Flush): Verificar aplicação dessa lógica no initialValues do Form
  const [dateRange] = useState<DateRange>({
    startDate: subMonths(startOfDay(now), 36),
    endDate: startOfDay(now),
  })

  const handleEquipeResponsavelChange = () => {
    form.change(path.microareas.absolutePath(), null)
  }

  form.change('unidadeResponsavelId', acesso?.unidadeSaude?.id)
  form.change('equipeResponsavelId', acesso?.equipe?.id)
  form.change('isCoordenador', isCoordenador)

  const handleOnSubmit = () => {
    const currentValues = form.getState().values

    const analyticsListasTematicas: Record<AcompanhamentoCondicaoSaudeTematicaEnum, () => void> = {
      DESENVOLVIMENTO_INFANTIL: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_des_infantil'),
      DIABETES: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_diabetes'),
      GERAL: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_geral'),
      GESTANTE_PUERPERA: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_gestacao_puerperio'),
      HIPERTENSAO_ARTERIAL: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_hipertensao'),
      PESSOA_IDOSA: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_pessoa_idosa'),
      SAUDE_BUCAL: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_saude_bucal'),
      SAUDE_MULHER: () => analytics.logEvent('buscar_acomp_condicoes_saude_lista_saude_mulher'),
    }

    analyticsListasTematicas[currentValues?.listaTematica]()

    const hasPeriodoUltimoAtendimento =
      currentValues?.periodoUltimoAtendimento?.startDate || currentValues?.periodoUltimoAtendimento?.endDate

    const hasCiap = !isEmpty(currentValues?.problemasCondicoes?.find((item) => isCiap(item)))
    const hasCid = !isEmpty(currentValues?.problemasCondicoes?.find((item) => !isCiap(item)))
    const hasIvcf = !isUndefinedOrNull(currentValues?.ivcf)

    !isEmpty(currentValues.grupoCondicao) && analytics.logEvent('buscar_acomp_condicoes_saude_with_grupo_condicao')
    hasCiap && analytics.logEvent('buscar_acomp_condicoes_saude_with_ciap')
    hasCid && analytics.logEvent('buscar_acomp_condicoes_saude_with_cid')
    hasPeriodoUltimoAtendimento && analytics.logEvent('buscar_acomp_condicoes_saude_with_periodo_ultimo_atend')

    currentValues.ativoListaProblema
      ? analytics.logEvent('buscar_acomp_condicoes_saude_with_condicoes_ativas')
      : analytics.logEvent('buscar_acomp_condicoes_saude_with_condicoes_ativas_e_inativas')

    hasIvcf && analytics.logEvent('buscar_acomp_condicoes_saude_with_ivcf')

    setListaTematica(currentValues?.listaTematica)
    handleSubmit()
  }

  const handleChangeCiapCid = (ciapCidList: CiapCidSelectModel[]) => {
    if (isEmpty(ciapCidList) && isEmpty(values.grupoCondicao)) {
      form.change(path.ativoListaProblema.absolutePath(), false)
    }
  }

  const handleChangeListaTematica = (listaSelecionada: AcompanhamentoCondicaoSaudeTematicaEnum) => {
    if (listaSelecionada === values.listaTematica) return

    form.change(path.listaTematica.absolutePath(), listaSelecionada)

    if (listaSelecionada !== AcompanhamentoCondicaoSaudeTematicaEnum.GERAL) {
      form.change(path.tria.absolutePath(), undefined)
      form.change(path.tipoVulnerabilidadeSelected.absolutePath(), undefined)
      isAbleToBuscar && handleOnSubmit()
    }
  }

  const handleResetFilterToTematicaDefault = () => {
    const filtersDefault = filterDefaultAcompanhamentoCondicaoSaude[values.listaTematica] ?? initialValues
    form.change('', { ...values, ...filtersDefault })
  }

  const handleFiltrosAvancadosClick = () => {
    history.push(`${match.url}${FILTROS_AVANCADOS_URL}`)
  }

  const handleSubmitFiltrosAvancados = (modalValues: AcompanhamentoCondicaoSaudeFormModel) => {
    form.change(path.ivcf.absolutePath(), modalValues.ivcf)
    form.change(path.periodoUltimoAtendimento.absolutePath(), modalValues.periodoUltimoAtendimento)
    form.change(path.problemasCondicoes.absolutePath(), modalValues.problemasCondicoes)
    form.change(path.tipoFaixaEtaria.absolutePath(), modalValues.tipoFaixaEtaria)
    form.change(path.faixaEtariaRange.absolutePath(), modalValues.faixaEtariaRange)
    form.change(path.sexo.absolutePath(), modalValues.sexo)
    form.change(path.identidadeGenero.absolutePath(), modalValues.identidadeGenero)
    form.change(path.racaCor.absolutePath(), modalValues.racaCor)
    form.change(path.grupoCondicao.absolutePath(), modalValues.grupoCondicao)
    form.change(path.ativoListaProblema.absolutePath(), modalValues.ativoListaProblema)
  }

  const countFiltrosAplicados = useMemo(() => {
    return contarFiltrosAvancadosAplicados(values)
  }, [values])

  const isFilterDefault = countFiltrosAplicados === 0

  return (
    <VFlow vSpacing={2}>
      <Grid wrap>
        <Cell size={3} style={styles.cell}>
          <FormControl label='Unidade responsável'>
            <Text>{acesso?.unidadeSaude?.nome.titleCase()}</Text>
          </FormControl>
        </Cell>
        <Cell size={isCoordenador ? 4 : 3} style={styles.cell}>
          <EquipeResponsavelField
            name={path.equipeResponsavel}
            equipe={acesso?.equipe}
            unidadeSaudeId={values?.unidadeResponsavelId}
            hasAccessToTodasEquipes={isCoordenador}
            placeholder='Selecione uma equipe'
            required={isCoordenador}
            onClear={() => onClearField(form, path.equipeResponsavel.absolutePath())}
            onChange={handleEquipeResponsavelChange}
          />
        </Cell>
        <Cell size={3} style={styles.cell}>
          <MicroareaSelectField
            name={path.microareas}
            label='Microárea'
            metaEquipe={path.equipeResponsavel}
            placeholder='Todas as microáreas'
            disabled={isCoordenador && !values?.equipeResponsavel}
          />
        </Cell>
        <Cell size={12}>
          <ListaTematicaSection name={path.listaTematica} handleChangeListaTematica={handleChangeListaTematica} />
        </Cell>
        <Cell size={12}>
          <Grid>
            {values.listaTematica === AcompanhamentoCondicaoSaudeTematicaEnum.GERAL && (
              <Fragment>
                <AcompanhamentoCondicaoSaudeProblemasCondicoesFilter
                  permission={Permissions.acompanhamentos.condicoesDeSaude.problemasECondicoes}
                  path={path}
                  canCheckApenasProblemasCondicoesAtivas={canCheckApenasProblemasCondicoesAtivas}
                  handleChangeCiapCid={handleChangeCiapCid}
                />
                <Cell size={12}>
                  <Heading level={2}>Informações da pessoa</Heading>
                </Cell>
                <Cell size={6}>
                  <SexoIdentidadeGeneroButtonGroup onChange={form.change} meta={path} />
                </Cell>
                <Cell size={6}>
                  <DateRangeField
                    name={path.periodoUltimoAtendimento.absolutePath()}
                    label='Período do último atendimento'
                    // TODO eldorado #916 (bold): corrigir as props startPlaceholder e finalPlaceholder
                    // startPlaceholder='Data inicial'
                    // finalPlaceholder='Data final'
                    initialValue={dateRange}
                    maxDate={now}
                  />
                </Cell>
                <Fragment>
                  <Cell size={12}>
                    <Heading level={4}>Habilitar filtros:</Heading>
                  </Cell>
                  <Cell size={12}>
                    <VulnerabilidadeFilterGroup meta={path} />
                  </Cell>
                </Fragment>
                <Cell size={5}>
                  <VFlow>
                    <Heading level={5}>Raça/Cor</Heading>
                    <RacaCorSelectField name={path.racaCor}></RacaCorSelectField>
                  </VFlow>
                </Cell>
                <Cell size={12}>
                  <FaixaEtariaFilterGroup
                    change={form.change}
                    meta={path}
                    faixaEtariaFilter={values?.tipoFaixaEtaria}
                    tipoVulnerabilidade={values?.tipoVulnerabilidadeSelected}
                  />
                </Cell>
              </Fragment>
            )}
            {values.listaTematica !== AcompanhamentoCondicaoSaudeTematicaEnum.GERAL && (
              <Cell size={12}>
                <Button kind='primary' size='small' skin='outline' onClick={handleFiltrosAvancadosClick}>
                  <Icon icon='filterOutline' />
                  Filtros avançados {!isFilterDefault && `(${countFiltrosAplicados})`}
                </Button>
                <Switch>
                  <Route exact path={`${match.url}${FILTROS_AVANCADOS_URL}`}>
                    <AcompanhamentoCondicaoSaudeFiltrosAvancadosModal
                      permission={Permissions.acompanhamentos.condicoesDeSaude.problemasECondicoes}
                      dateRange={dateRange}
                      now={now}
                      initialValues={values}
                      onChangeCiapCid={handleChangeCiapCid}
                      onSubmit={handleSubmitFiltrosAvancados}
                    />
                  </Route>
                </Switch>
              </Cell>
            )}
          </Grid>
        </Cell>
      </Grid>
      <SearchFooter
        handleSubmit={handleOnSubmit}
        isAbleToBuscar={isAbleToBuscar}
        isFilterDefault={isFilterDefault}
        onResetToDefault={handleResetFilterToTematicaDefault}
      />
    </VFlow>
  )
}

const styles = {
  cell: css`
    margin-top: 1rem;
  `,
}
